(() => {
  if (window.HBPlantingPlan) return;

  const STORAGE_KEY = 'hbPlantingPlan:v1';
  const MAX_ITEMS = 100;
  const state = {
    items: [],
    selected: new Set(),
    open: false,
    busy: false,
    previousFocus: null
  };

  const selectors = {
    drawer: '[data-hb-plan-drawer]',
    backdrop: '[data-hb-plan-backdrop]',
    items: '[data-hb-plan-items]',
    empty: '[data-hb-plan-empty]',
    toolbar: '[data-hb-plan-toolbar]',
    footer: '[data-hb-plan-footer]',
    summary: '[data-hb-plan-summary]',
    total: '[data-hb-plan-total]',
    status: '[data-hb-plan-status]',
    template: '[data-hb-plan-item-template]'
  };
  const feedbackTimers = new WeakMap();
  let initialized = false;

  const asText = (value) => String(value == null ? '' : value);
  const clampQuantity = (value) => Math.min(99, Math.max(1, Number.parseInt(value, 10) || 1));
  const itemKey = (item) => asText(item.productId);

  function readItems() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      return parsed
        .filter((item) => item && item.productId && item.variantId && item.title)
        .filter((item) => {
          const key = itemKey(item);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, MAX_ITEMS)
        .map((item) => ({
          productId: asText(item.productId),
          variantId: asText(item.variantId),
          title: asText(item.title),
          variantTitle: asText(item.variantTitle),
          url: asText(item.url),
          image: asText(item.image),
          price: Math.max(0, Number(item.price) || 0),
          quantity: clampQuantity(item.quantity),
          currency: asText(item.currency || 'GBP'),
          addedAt: Number(item.addedAt) || Date.now()
        }));
    } catch (_error) {
      return [];
    }
  }

  function writeItems() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch (_error) {
      setStatus('This browser could not save your planting plan.', true);
    }
    document.dispatchEvent(new CustomEvent('hb:planting-plan:changed', {
      detail: { count: state.items.length }
    }));
  }

  function formatMoney(amount, currency = 'GBP') {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format((Number(amount) || 0) / 100);
    } catch (_error) {
      return `\u00A3${((Number(amount) || 0) / 100).toFixed(2)}`;
    }
  }

  function findVariantData(button, variantId) {
    const productId = button.dataset.hbProductId;
    const scripts = document.querySelectorAll('[data-hb-plan-variants-for]');
    const script = Array.from(scripts).find((node) => node.dataset.hbPlanVariantsFor === productId);
    if (!script) return null;

    try {
      const variants = JSON.parse(script.textContent || '[]');
      return variants.find((variant) => asText(variant.id) === asText(variantId)) || null;
    } catch (_error) {
      return null;
    }
  }

  function currentVariantId(button) {
    if (button.dataset.hbPlanContext !== 'product') return button.dataset.hbVariantId;
    const productContainer = button.closest('[id^="x-product-template-"]') || button.closest('.section-product-info');
    const variantInput = productContainer && productContainer.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
    return variantInput && variantInput.value ? variantInput.value : button.dataset.hbVariantId;
  }

  function itemFromButton(button) {
    const variantId = currentVariantId(button);
    const variant = findVariantData(button, variantId);
    const variantImage = variant && variant.featured_image;
    const image = typeof variantImage === 'string'
      ? variantImage
      : variantImage && variantImage.src
        ? variantImage.src
        : button.dataset.hbProductImage;

    return {
      productId: asText(button.dataset.hbProductId),
      variantId: asText(variantId),
      title: asText(button.dataset.hbProductTitle),
      variantTitle: asText(variant ? variant.title : button.dataset.hbVariantTitle),
      url: asText(button.dataset.hbProductUrl),
      image: asText(image),
      price: Math.max(0, Number(variant ? variant.price : button.dataset.hbVariantPrice) || 0),
      quantity: 1,
      currency: asText(button.dataset.hbCurrency || 'GBP'),
      addedAt: Date.now()
    };
  }

  function isSaved(productId) {
    return state.items.some((item) => item.productId === asText(productId));
  }

  function syncButton(button) {
    const saved = isSaved(button.dataset.hbProductId);
    const title = saved ? 'Remove from My Planting Plan' : 'Add to My Planting Plan';
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('title', title);
    button.setAttribute('aria-label', `${title}: ${button.dataset.hbProductTitle || ''}`.trim());
    const label = button.querySelector('[data-hb-plan-label]');
    if (label) label.textContent = saved ? 'Saved to My Planting Plan' : 'Add to My Planting Plan';
  }

  function syncButtons(root = document) {
    if (root.matches && root.matches('[data-hb-plan-toggle]')) syncButton(root);
    if (root.querySelectorAll) root.querySelectorAll('[data-hb-plan-toggle]').forEach(syncButton);
  }

  function showToggleFeedback(button, saved) {
    const feedback = button.querySelector('[data-hb-plan-feedback]');
    const liveRegion = button.querySelector('[data-hb-plan-live]');
    const message = saved ? 'Added to My Planting Plan' : 'Removed from My Planting Plan';

    if (liveRegion) {
      liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        liveRegion.textContent = message;
      });
    }
    if (!feedback) return;

    const currentTimer = feedbackTimers.get(button);
    if (currentTimer) window.clearTimeout(currentTimer);
    feedback.textContent = message;
    button.classList.remove('is-confirming');
    void button.offsetWidth;
    button.classList.add('is-confirming');
    feedbackTimers.set(button, window.setTimeout(() => {
      button.classList.remove('is-confirming');
      feedbackTimers.delete(button);
    }, 1800));
  }

  function updateHeaderCount() {
    document.querySelectorAll('[data-hb-plan-count]').forEach((badge) => {
      badge.textContent = state.items.length > 99 ? '99+' : asText(state.items.length);
      badge.hidden = state.items.length === 0;
    });
  }

  function setStatus(message = '', isError = false) {
    const status = document.querySelector(selectors.status);
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', Boolean(isError));
  }

  function totalPrice() {
    return state.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }

  function render() {
    const container = document.querySelector(selectors.items);
    const template = document.querySelector(selectors.template);
    if (!container || !template) return;

    container.replaceChildren();
    const fragment = document.createDocumentFragment();

    state.items.forEach((item) => {
      const node = template.content.firstElementChild.cloneNode(true);
      const key = itemKey(item);
      node.dataset.hbPlanItem = key;

      node.querySelectorAll('[data-hb-plan-item-link]').forEach((link) => {
        link.href = item.url || '#';
      });

      const image = node.querySelector('[data-hb-plan-item-image]');
      image.src = item.image || '';
      image.alt = item.title;
      image.hidden = !item.image;

      node.querySelector('[data-hb-plan-item-title]').textContent = item.title;
      const variant = node.querySelector('[data-hb-plan-item-variant]');
      const hasVariantTitle = item.variantTitle && item.variantTitle.toLowerCase() !== 'default title';
      variant.textContent = hasVariantTitle ? item.variantTitle : '';
      variant.hidden = !hasVariantTitle;

      node.querySelector('[data-hb-plan-item-price]').textContent = formatMoney(item.price * item.quantity, item.currency);
      const quantity = node.querySelector('[data-hb-plan-quantity]');
      quantity.value = item.quantity;

      const checkbox = node.querySelector('[data-hb-plan-select-item]');
      checkbox.checked = state.selected.has(key);
      checkbox.setAttribute('aria-label', `Select ${item.title}`);

      node.querySelector('[data-hb-plan-remove]').setAttribute('aria-label', `Remove ${item.title} from My Planting Plan`);
      fragment.appendChild(node);
    });

    container.appendChild(fragment);
    const hasItems = state.items.length > 0;
    const empty = document.querySelector(selectors.empty);
    const toolbar = document.querySelector(selectors.toolbar);
    const footer = document.querySelector(selectors.footer);
    if (empty) empty.hidden = hasItems;
    if (toolbar) toolbar.hidden = !hasItems;
    if (footer) footer.hidden = !hasItems;

    const summary = document.querySelector(selectors.summary);
    if (summary) summary.textContent = `${state.items.length} ${state.items.length === 1 ? 'variety' : 'varieties'}`;
    const total = document.querySelector(selectors.total);
    if (total) total.textContent = formatMoney(totalPrice(), state.items[0] && state.items[0].currency);

    const selectAll = document.querySelector('[data-hb-plan-select-all]');
    if (selectAll) {
      const allSelected = hasItems && state.items.every((item) => state.selected.has(itemKey(item)));
      selectAll.textContent = allSelected ? 'Deselect all' : 'Select all';
    }

    const addSelected = document.querySelector('[data-hb-plan-add-selected]');
    if (addSelected) addSelected.disabled = state.busy || state.selected.size === 0;
    const addAll = document.querySelector('[data-hb-plan-add-all]');
    if (addAll) addAll.disabled = state.busy || !hasItems;

    updateHeaderCount();
    syncButtons();
  }

  function saveAndRender() {
    writeItems();
    render();
  }

  function toggleItem(button) {
    const productId = asText(button.dataset.hbProductId);
    const index = state.items.findIndex((item) => item.productId === productId);
    if (index >= 0) {
      const [removed] = state.items.splice(index, 1);
      state.selected.delete(itemKey(removed));
    } else {
      const item = itemFromButton(button);
      state.items.unshift(item);
      state.selected.add(itemKey(item));
    }
    setStatus('');
    saveAndRender();
    showToggleFeedback(button, index < 0);
  }

  function openDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    const backdrop = document.querySelector(selectors.backdrop);
    if (!drawer || !backdrop) return;
    state.previousFocus = document.activeElement;
    state.open = true;
    render();
    backdrop.hidden = false;
    drawer.classList.add('is-open');
    backdrop.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-hb-plan-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('hb-plan-open');
    const closeButton = drawer.querySelector('[data-hb-plan-close]');
    if (closeButton) {
      try {
        closeButton.focus({ preventScroll: true });
      } catch (_error) {
        closeButton.focus();
      }
    }
  }

  function closeDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    const backdrop = document.querySelector(selectors.backdrop);
    if (!drawer || !backdrop) return;
    state.open = false;
    drawer.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-hb-plan-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('hb-plan-open');
    window.setTimeout(() => {
      if (!state.open) backdrop.hidden = true;
    }, 260);
    if (state.previousFocus && typeof state.previousFocus.focus === 'function') {
      try {
        state.previousFocus.focus({ preventScroll: true });
      } catch (_error) {
        state.previousFocus.focus();
      }
    }
  }

  function updateItemQuantity(key, value) {
    const item = state.items.find((entry) => itemKey(entry) === key);
    if (!item) return;
    item.quantity = clampQuantity(value);
    saveAndRender();
  }

  function updateCartCount(cart) {
    const count = Number(cart && cart.item_count) || 0;
    document.querySelectorAll('#cart-icon-bubble, #mobile-cart-icon-bubble').forEach((bubble) => {
      bubble.dataset.value = asText(count);
      const badge = bubble.querySelector('div');
      if (badge) badge.classList.toggle('hidden', count === 0);
      const value = bubble.querySelector('span');
      if (value) value.textContent = count > 99 ? '99+' : asText(count);
    });
  }

  async function addItemsToCart(items, sourceButton) {
    if (!items.length || state.busy) return;
    state.busy = true;
    setStatus('Adding to cart...');
    render();
    const originalText = sourceButton.textContent;
    sourceButton.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: item.variantId,
            quantity: clampQuantity(item.quantity)
          }))
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Some varieties could not be added to the cart.');
      }

      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      if (cart) updateCartCount(cart);
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      setStatus(`${items.length} ${items.length === 1 ? 'variety' : 'varieties'} added to cart.`);
    } catch (error) {
      setStatus(error && error.message ? error.message : 'The planting plan could not be added to the cart.', true);
    } finally {
      state.busy = false;
      sourceButton.textContent = originalText;
      render();
    }
  }

  function handleDrawerClick(target) {
    const itemNode = target.closest('[data-hb-plan-item]');
    const key = itemNode && itemNode.dataset.hbPlanItem;

    if (target.closest('[data-hb-plan-remove]') && key) {
      const index = state.items.findIndex((item) => itemKey(item) === key);
      if (index >= 0) state.items.splice(index, 1);
      state.selected.delete(key);
      saveAndRender();
      return true;
    }

    if (target.closest('[data-hb-plan-quantity-minus]') && key) {
      const item = state.items.find((entry) => itemKey(entry) === key);
      if (item) updateItemQuantity(key, item.quantity - 1);
      return true;
    }

    if (target.closest('[data-hb-plan-quantity-plus]') && key) {
      const item = state.items.find((entry) => itemKey(entry) === key);
      if (item) updateItemQuantity(key, item.quantity + 1);
      return true;
    }

    if (target.closest('[data-hb-plan-select-all]')) {
      const allSelected = state.items.length && state.items.every((item) => state.selected.has(itemKey(item)));
      state.selected = new Set(allSelected ? [] : state.items.map(itemKey));
      render();
      return true;
    }

    if (target.closest('[data-hb-plan-clear]')) {
      state.items = [];
      state.selected.clear();
      saveAndRender();
      return true;
    }

    const addSelected = target.closest('[data-hb-plan-add-selected]');
    if (addSelected) {
      addItemsToCart(state.items.filter((item) => state.selected.has(itemKey(item))), addSelected);
      return true;
    }

    const addAll = target.closest('[data-hb-plan-add-all]');
    if (addAll) {
      addItemsToCart(state.items, addAll);
      return true;
    }

    return false;
  }

  function handleClick(event) {
    const target = event.target && event.target.closest
      ? event.target
      : event.target && event.target.parentElement;
    if (!target) return;
    const toggle = target.closest('[data-hb-plan-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleItem(toggle);
      return;
    }

    if (target.closest('[data-hb-plan-open]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (target.closest('[data-hb-plan-close]') || target.closest('[data-hb-plan-backdrop]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (handleDrawerClick(target)) event.preventDefault();
  }

  function handleChange(event) {
    const target = event.target && event.target.closest
      ? event.target
      : event.target && event.target.parentElement;
    if (!target) return;
    const checkbox = target.closest('[data-hb-plan-select-item]');
    if (checkbox) {
      const item = checkbox.closest('[data-hb-plan-item]');
      if (!item) return;
      if (checkbox.checked) state.selected.add(item.dataset.hbPlanItem);
      else state.selected.delete(item.dataset.hbPlanItem);
      render();
      return;
    }

    const quantity = target.closest('[data-hb-plan-quantity]');
    if (quantity) {
      const item = quantity.closest('[data-hb-plan-item]');
      if (item) updateItemQuantity(item.dataset.hbPlanItem, quantity.value);
    }
  }

  function trapFocus(event) {
    if (!state.open || event.key !== 'Tab') return;
    const drawer = document.querySelector(selectors.drawer);
    if (!drawer) return;
    const focusable = Array.from(drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function init() {
    if (initialized || !document.querySelector(selectors.drawer)) return;
    initialized = true;
    state.items = readItems();
    state.selected = new Set(state.items.map(itemKey));
    render();
    document.addEventListener('click', handleClick);
    document.addEventListener('change', handleChange);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && state.open) closeDrawer();
      trapFocus(event);
    });
    window.addEventListener('pageshow', () => {
      state.items = readItems();
      state.selected = new Set(state.items.map(itemKey));
      render();
    });
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      state.items = readItems();
      state.selected = new Set(state.items.map(itemKey));
      render();
    });
    document.addEventListener('shopify:section:load', (event) => syncButtons(event.target));

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node.matches('[data-hb-plan-toggle]') || node.querySelector('[data-hb-plan-toggle]'))) {
            syncButtons(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.HBPlantingPlan = { open: openDrawer, close: closeDrawer };
  if (document.querySelector(selectors.drawer)) init();
  else if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
